import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { LanguageService } from '../../../../core/services/utils/language.service';
import { ModalComponent } from '../../../ui/modal/modal.component';
import {
  DataColumn,
  DataTableComponent,
} from '../../../ui/data-table/data-table.component';
import {
  SearchableSelectComponent,
  SelectItem,
} from '../../../ui/searchable-select/searchable-select.component';
import { FilterBarComponent } from '../../../ui/filter-bar/filter-bar.component';
import { AddStudentComponent } from '../../../../shared/modals/admin/add-student/add-student.component';
import { StudentService } from '../../../../core/services/student.service';
import { LearningService } from '../../../../core/services/learning-path.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { ReadOnlyNoticeComponent } from '../../../ui/read-only-notice/read-only-notice.component';
import {
  Student,
  CreateStudentDto,
  UpdateStudentDto,
} from '../../../../core/interfaces/student';
import { LearningPath } from '../../../../core/interfaces/learning-path';
import { Subject as RxJsSubject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { matchesSearch } from '../../../../core/utils/search-text';

/**
 * Liste des élèves (admin) — écran **pilote** de la table de données partagée.
 *
 * <p>La grille Syncfusion a été remplacée par {@link DataTableComponent} :
 * même style que la banque de questions, tri côté client, états de chargement /
 * vide / erreur cohérents, et surtout une dépendance tierce en moins sur cet
 * écran. La logique métier (CRUD, modale, filtres par parcours) est inchangée.</p>
 */
@Component({
  selector: 'app-students',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ModalComponent,
    AddStudentComponent,
    DataTableComponent,
    SearchableSelectComponent,
    FilterBarComponent,
    ReadOnlyNoticeComponent,
  ],
  templateUrl: './students.component.html',
  styleUrl: './students.component.css',
})
export class StudentsComponent implements OnInit, OnDestroy {
  /**
   * Créer, modifier et supprimer une fiche demandent `students.write` ; la
   * route s'ouvre avec la lecture. Sans l'écriture, l'écran passe en
   * consultation : le formulaire ne s'ouvre pas — il interroge dès la saisie
   * du code une route d'écriture, dont le refus éjectait de l'écran.
   */
  readonly canWrite = inject(AuthService).hasPermission('students.write');

  private translate = inject(TranslateService);
  private languageService = inject(LanguageService);
  private studentService = inject(StudentService);
  private learningService = inject(LearningService);
  private swalService = inject(SwalService);
  private router = inject(Router);
  private destroy$ = new RxJsSubject<void>();

  students: Student[] = [];
  learningPaths: LearningPath[] = [];
  selectedStudent: Student | null = null;
  selectedPathId: number | null = null;
  isRTL = false;
  currentLocale = 'ar';
  isLoading = false;
  isLoadingPaths = false;
  isSaving = false;
  isEditMode = false;
  showStudentModal = false;
  hasError = false;

  /** Tri côté client : colonne active et sens. `null` = ordre serveur. */
  sortBy: string | null = null;
  sortDescending = false;

  /** Colonnes de la table partagée — mêmes clés i18n que l'ancienne grille. */
  readonly columns: DataColumn[] = [
    { key: 'firstName', labelKey: 'DATA_TABLE.FIRST_NAME', sortable: true },
    { key: 'lastName', labelKey: 'DATA_TABLE.LAST_NAME', sortable: true },
    { key: 'email', labelKey: 'DATA_TABLE.EMAIL', sortable: true },
    { key: 'learningPath', labelKey: 'DATA_TABLE.LEARNING_PATH', sortable: true },
    { key: 'status', labelKey: 'ADMIN.STATUS', sortable: true, align: 'center' },
    { key: 'actions', labelKey: 'DATA_TABLE.ACTIONS', align: 'center' },
  ];

  /** Options du sélecteur de parcours, matérialisées (pas un getter : la liste
   *  serait reconstruite à chaque cycle de détection). */
  pathOptions: SelectItem[] = [];

  /** Recherche plein-texte + filtre de statut (côté client). */
  search = '';
  statusFilter: 'all' | 'active' | 'inactive' = 'all';

  /** Nombre de facettes actives — pilote le compteur et la remise à zéro. */
  get activeFilterCount(): number {
    let n = 0;
    if (this.selectedPathId != null) n++;
    if (this.statusFilter !== 'all') n++;
    if (this.search.trim()) n++;
    return n;
  }

  ngOnInit(): void {
    this.currentLocale = this.languageService.getCurrentLang();
    this.isRTL = !this.languageService.isLTR();
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: LangChangeEvent) => {
        this.currentLocale = event.lang;
        this.isRTL = !this.languageService.isLTR();
      });
    this.loadLearningPaths();
    // La liste ne dépend pas du référentiel : attendre les parcours pour la
    // demander la laissait vide, sans message, dès qu'ils échouaient.
    this.loadStudents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onStatusFilter(value: 'all' | 'active' | 'inactive'): void {
    this.statusFilter = value;
  }

  /**
   * État vide : distingue « aucun résultat pour ces filtres » de « aucun
   * élève ». Les trois facettes comptent — le parcours aussi : filtré par le
   * serveur, il renvoyait une liste vide que l'écran prenait pour une école
   * sans élèves, et proposait d'en créer un premier.
   */
  get emptyTitleKey(): string {
    return this.activeFilterCount > 0
      ? 'FILTERS.EMPTY_TITLE'
      : 'ADMIN.NO_STUDENTS_FOUND';
  }
  get emptyMessageKey(): string {
    return this.activeFilterCount > 0
      ? 'FILTERS.EMPTY_MESSAGE'
      : 'ADMIN.ADD_FIRST_STUDENT';
  }

  /** Élèves après recherche + filtre statut, puis tri localisé. */
  get visibleStudents(): Student[] {
    let list = this.students;

    if (this.statusFilter !== 'all') {
      const wantActive = this.statusFilter === 'active';
      list = list.filter((s) => !!s.isActive === wantActive);
    }

    if (this.search.trim()) {
      list = list.filter((s) =>
        matchesSearch(this.search, s.firstName, s.lastName, s.email),
      );
    }

    return this.sortList(list);
  }

  /** Tri localisé selon la colonne active ; ordre serveur si aucune. */
  private sortList(list: Student[]): Student[] {
    const key = this.sortBy;
    if (!key) return list;

    const dir = this.sortDescending ? -1 : 1;
    const value = (s: Student): string => {
      switch (key) {
        case 'learningPath':
          return s.learningPath?.title ?? '';
        case 'status':
          return s.isActive ? '1' : '0';
        default:
          return String((s as unknown as Record<string, unknown>)[key] ?? '');
      }
    };

    return [...list].sort(
      (a, b) =>
        value(a).localeCompare(value(b), this.currentLocale, {
          numeric: true,
          sensitivity: 'base',
        }) * dir,
    );
  }

  onSort(key: string): void {
    if (this.sortBy === key) {
      this.sortDescending = !this.sortDescending;
    } else {
      this.sortBy = key;
      this.sortDescending = false;
    }
  }


  loadLearningPaths(): void {
    this.isLoadingPaths = true;
    this.learningService
      .getLearningPaths(true, false)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingPaths = false)),
      )
      .subscribe({
        next: (response) => {
          this.learningPaths = response.data;
          this.pathOptions = this.learningPaths.map((p) => ({
            id: p.id,
            label: p.title,
          }));
        },
        error: (error) => {
          console.error('Error loading learning paths:', error);
          this.swalService.warning(
            this.translate.instant('ADMIN.ERROR_LOADING_PATHS'),
          );
        },
      });
  }

  /** Requête de liste en cours — une seule à la fois. */
  private studentsRequest?: Subscription;

  /** Sans parcours sélectionné, l'API renvoie tous les élèves. */
  loadStudents(): void {
    // Changer de parcours deux fois de suite lançait deux requêtes : la plus
    // lente gagnait, et la liste ne correspondait plus au parcours affiché.
    // À annuler avant de poser `isLoading`, que son `finalize` relâche.
    this.studentsRequest?.unsubscribe();
    this.isLoading = true;
    this.hasError = false;
    this.studentsRequest = this.studentService
      .getStudents(this.selectedPathId ?? undefined)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (response) => {
          this.students = response.data;
        },
        error: (error) => {
          console.error('Error loading students:', error);
          this.hasError = true;
        },
      });
  }

  onPathChange(pathId: number | null): void {
    this.selectedPathId = pathId;
    this.loadStudents();
  }

  onSearchChange(text: string): void {
    this.search = text;
  }

  resetFilters(): void {
    this.search = '';
    this.statusFilter = 'all';
    if (this.selectedPathId != null) {
      this.selectedPathId = null;
      this.loadStudents();
    }
  }

  addStudent(): void {
    // Aucun pré-requis de filtre : le parcours se choisit dans le modal, qui
    // se contente de proposer celui du filtre courant par défaut.
    this.selectedStudent = null;
    this.isEditMode = false;
    this.showStudentModal = true;
  }

  onEditStudent(student: Student): void {
    this.selectedStudent = { ...student };
    this.isEditMode = true;
    this.showStudentModal = true;
  }

  onDeleteStudent(student: Student): void {
    this.swalService.confirm(
      this.translate.instant('ADMIN.CONFIRM_DELETE_STUDENT_MESSAGE'),
      () => {
        this.deleteStudent(student.id);
      },
    );
  }

  private deleteStudent(studentId: number): void {
    this.studentService
      .deleteStudent(studentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.swalService.success(
            this.translate.instant('SWAL.DELETE_SUCCESS'),
          );
          this.loadStudents();
        },
        error: (error) => {
          console.error('Error deleting student:', error);
          this.swalService.error(this.translate.instant('SWAL.ERROR'));
        },
      });
  }

  onShowStudent(student: Student): void {
    this.selectedStudent = { ...student };

    const activeStatus = student.isActive
      ? this.translate.instant('ADMIN.ACTIVE')
      : this.translate.instant('ADMIN.INACTIVE');

    const enrollmentDate = student.enrollmentDate
      ? this.formatDate(student.enrollmentDate)
      : 'N/A';

    this.swalService.info(
      `<div style="text-align: start;">
        <strong>${this.translate.instant('DATA_TABLE.FIRST_NAME')}:</strong> ${student.firstName}<br>
        <strong>${this.translate.instant('DATA_TABLE.LAST_NAME')}:</strong> ${student.lastName}<br>
        <strong>${this.translate.instant('DATA_TABLE.EMAIL')}:</strong> ${student.email}<br>
        <strong>${this.translate.instant('DATA_TABLE.LEARNING_PATH')}:</strong> ${student.learningPath?.title || 'N/A'}<br>
        <strong>${this.translate.instant('DATA_TABLE.ENROLLMENT_DATE')}:</strong> ${enrollmentDate}<br>
        <strong>${this.translate.instant('ADMIN.STATUS')}:</strong> ${activeStatus}
      </div>`,
      `${student.firstName} ${student.lastName}`,
    );
  }

  handleStudentSaved(studentData: CreateStudentDto | UpdateStudentDto): void {
    this.isSaving = true;

    const isUpdate = 'id' in studentData && studentData.id !== undefined;

    const operation = isUpdate
      ? this.studentService.updateStudent(
          (studentData as UpdateStudentDto).id,
          studentData as UpdateStudentDto,
        )
      : this.studentService.createStudent(studentData as CreateStudentDto);

    operation
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: () => {
          const messageKey = isUpdate
            ? 'ADMIN.STUDENT_UPDATED_SUCCESS'
            : 'ADMIN.STUDENT_CREATED_SUCCESS';
          this.swalService.success(this.translate.instant(messageKey));
          this.closeModal();
          this.loadStudents();
        },
        error: (error) => {
          console.error('Error saving student:', error);

          // Un 409 n'est pas une panne : c'est le serveur qui refuse un
          // doublon. Le dire tel quel évite de renvoyer l'administrateur vers
          // le support pour un code élève déjà pris — d'autant que la liste
          // vient d'être rechargée et montre peut-être déjà le doublon.
          const errorKey =
            error?.status === 409
              ? 'ADMIN.ERROR_STUDENT_CONFLICT'
              : isUpdate
                ? 'ADMIN.ERROR_UPDATING_STUDENT'
                : 'ADMIN.ERROR_CREATING_STUDENT';

          this.swalService.error(this.translate.instant(errorKey));
        },
      });
  }

  onModalClosed(): void {
    this.closeModal();
  }

  private closeModal(): void {
    this.showStudentModal = false;
    this.selectedStudent = null;
    this.isEditMode = false;
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString(this.currentLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
}
